// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// TWO ENGINES — who the land engine actually belongs to.
//
// The ledger establishes that roughly three quarters of the return to farm
// capital came from the land rather than from farming. The obvious follow-up is
// "whose land, and whose farming?", and this view answers it three ways, each a
// coarser cut than the last: by what the farm produces (industry), by the
// country it sits in (agro-climatic zone), and region by region.
//
// Deliberately NOT here: the balance-sheet race (land vs receipts vs debt vs
// area on one baseline) belongs to the Mechanism view, and the per-region
// multiples belong to Trails. This view stays in RATE space — percent a year —
// throughout, so it never restates either of them in different units.
//
// Two rules govern nearly every decision below:
//
//   * The provisional year is drawn and never counted. It is hatched everywhere
//     it appears and is excluded from every average on the page.
//   * An average is taken over firm-or-fair evidence only, and the count of
//     what that discarded is printed beside it. That filter is not free — see
//     MIN_RANKABLE_YEARS for what it does to the pastoral regions.

import { loadCore, loadIndustry } from '../data';
import type { Core, IndustryData, IndustryYear, Region, RegionYear } from '../types';
import { CRIMSON, FARMING, GRAPHITE, LAND, NEUTRAL, SCOPE_SHORT, ZONE_COLOUR, ZONE_ORDER } from '../theme';
import {
  collapse, evidenceLines, isOutlineOnly, isRankable, isTokenSized,
  tierOpacity, tierPrefix, TIER_WORD, type Tier,
} from '../uncertainty';
import {
  axisBottom, axisLeft, brokenPath, chartFrame, chartSvg, el, extent, finite,
  hatchDefs, isolatedPoints, linearScale, markTip, niceTicks,
} from '../components/svg';
import { esc, fy, money, num, signedPct } from '../format';
import { glossIcon, glossTerm } from '../components/glossary';
import { closeButton, openOverlay } from '../overlay';
// The one shared region dossier. A view that built its own drill-down would be
// the fourth slightly different set of numbers for the same region.
import { openRegion } from '../components/regionDrawer';

/**
 * The published JSON carries an evidence tier on every region-year and a median
 * tier on every region. `types.ts` does not declare those two fields yet, so
 * they are widened here rather than by editing a shared file out from under the
 * other views. Both are optional, so this stays correct once types.ts catches up.
 */
type Cell = RegionYear & { tier?: Tier | null; apprTier?: Tier | null };
type Reg = Region & { tier?: Tier | null; weakYears?: number };

/**
 * How many firm-or-fair years a region needs before its ~35-year average is
 * drawn as a point on the scatter.
 *
 * Filtering to tier 1-2 is mandatory for any average on this site, but the
 * filter is not neutral: it keeps the years with the biggest samples, so a
 * region left with a handful of cells gets an "average" that is really one good
 * decade. WA Pilbara and Central Pastoral keeps 5 of its 36 years and those five
 * average -11%/yr, against a published all-years mean of -0.8%. Plotting that as
 * the region's average would be a worse lie than leaving it out, so regions
 * under this threshold are named and explained beneath the chart instead.
 */
const MIN_RANKABLE_YEARS = 10;

/** Industry tiles, in a fixed order — never sorted by any statistic. See the note in industryCard. */
const INDUSTRY_ORDER = ['All Broadacre', 'Cropping', 'Mixed', 'Sheep', 'Sheep-Beef', 'Beef', 'Dairy'];

interface Engines {
  region: Reg;
  /** Mean published rate of return EXCLUDING appreciation — what the farming earned. */
  farming: number | null;
  /** Mean of (including − excluding), in percentage POINTS — what the land added. */
  land: number | null;
  years: number;
  totalYears: number;
  tier: Tier;
  placed: boolean;
}

interface ZoneEngines {
  zone: string;
  farming: number | null;
  land: number | null;
  years: number;
  totalYears: number;
  regions: number;
}

export async function renderEngines(host: HTMLElement): Promise<void> {
  const [core, industry] = await Promise.all([loadCore(), loadIndustry()]);
  const lastSettled = core.settledYears[core.settledYears.length - 1];

  const engines = computeEngines(core);
  const placed = engines.filter((e) => e.placed);
  const unplaced = engines.filter((e) => !e.placed);

  const head = document.createElement('div');
  head.className = 'view-head';
  head.innerHTML = `
    <h1>The two engines</h1>
    <p class="lede">A ${glossTerm('broadacre', 'broadacre')} farm earns from two things at once: the farming, and the
      ground it stands on. Across the whole survey the second is much the larger. This view asks who
      that is actually true of — which kinds of farm, which parts of the country, which regions.</p>
    <p>Everything here is a ${glossTerm('rateOfReturn', 'rate of return')} in per cent a year, exactly as
      ABARES publishes it. Nothing on this page is a dollar multiple and nothing is recalculated from
      profit and capital. Averages stop at ${esc(fy(lastSettled))}; the
      ${glossTerm('provisional', 'provisional')} year is drawn, hatched, and never counted.</p>`;
  host.append(head);

  host.append(industryCard(industry, core, lastSettled));
  host.append(zoneCard(core));
  host.append(scatterCard(core, placed, unplaced, engines.length));

  const foot = document.createElement('div');
  foot.className = 'stack';
  foot.innerHTML = `
    <p class="note"><strong>What this view does not claim.</strong> Neither engine is calculated here.
      Both come straight from the two rate-of-return series ABARES publishes — one counting
      ${glossTerm('capitalAppreciation', 'capital appreciation')}, one not — and the land engine is
      simply the gap between them. Re-deriving a rate of return from profit and capital fails to
      reproduce the published series in a fifth to a half of all region-years, so this site never
      does it. No figure here is a total for a region either: every one is an average
      ${glossTerm('perFarm', 'per surveyed farm')}.</p>
    <p class="xsmall muted">${esc(SCOPE_SHORT)}</p>`;
  host.append(foot);
}

/* ══════════════════════════ 1 · by kind of farm ════════════════════════════ */

function industryCard(industry: IndustryData, core: Core, lastSettled: number): HTMLElement {
  const card = document.createElement('section');
  card.className = 'card stack';

  const names = INDUSTRY_ORDER.filter((n) => industry.industries.includes(n));
  const domain = sharedDomain(industry, names);

  card.innerHTML = `
    <div>
      <h2 class="card-title">Which kind of farm? ${glossIcon('rateOfReturn')}</h2>
      <p class="card-sub">One small chart per industry, all on the same vertical scale so they can be
        compared by eye. The <strong>teal line</strong> is the rate of return the farming earned. The
        <strong>ochre line</strong> is the rate of return once the change in the value of the farm's
        capital is counted too. The shaded space between them is the land's contribution: ochre where
        the land added to the return, crimson in the years it took some away. Click any chart to open
        that industry year by year.</p>
    </div>`;

  const grid = document.createElement('div');
  grid.className = 'grid';
  card.append(grid);

  for (const name of names) {
    const rows = industry.industrySeries[name] ?? [];
    const tile = document.createElement('div');
    tile.className = 'card stack';
    tile.style.gap = 'var(--space-xs)';
    tile.style.cursor = 'pointer';
    tile.setAttribute('role', 'button');
    tile.setAttribute('tabindex', '0');
    tile.setAttribute('aria-label', `${name}: open the two engines year by year`);

    const h = document.createElement('h3');
    h.className = 'card-title';
    h.style.fontSize = 'var(--font-size-base)';
    h.textContent = name === 'All Broadacre' ? 'All broadacre (the survey as a whole)' : name;
    const sub = document.createElement('p');
    sub.className = 'xsmall muted';
    sub.textContent = name === 'Dairy'
      ? 'From the dairy survey (ADIS), national only'
      : `${rows.filter((r) => r.year !== core.provisionalYear).length} settled years`;
    tile.append(h, sub);

    tile.append(engineChart(rows, {
      w: 300, h: 168, domain, prov: core.provisionalYear, lastSettled,
      label: `${name}: rate of return with and without capital appreciation, by year`,
      detailed: false, idSuffix: `t${names.indexOf(name)}`, industry: name,
    }));

    const open = () => openIndustry(name, rows, core, domain, lastSettled);
    tile.addEventListener('click', open);
    tile.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
    });
    grid.append(tile);
  }

  const legend = document.createElement('div');
  legend.className = 'legend';
  legend.innerHTML = `
    <span class="legend-item"><span class="legend-swatch" style="background:${esc(FARMING)}"></span>what the farming earned</span>
    <span class="legend-item"><span class="legend-swatch" style="background:${esc(LAND)}"></span>counting the land as well</span>
    <span class="legend-item"><span class="legend-swatch" style="background:${esc(LAND)};opacity:.32"></span>the land added to the return</span>
    <span class="legend-item"><span class="legend-swatch" style="background:${esc(CRIMSON)};opacity:.32"></span>the land took some away</span>
    <span class="legend-item"><span class="legend-swatch" style="background:repeating-linear-gradient(45deg,${esc(GRAPHITE)} 0 2px,transparent 2px 5px)"></span>provisional year, not counted</span>`;
  card.append(legend);

  const negNat = core.national.filter((n) => n.appreciation != null && n.appreciation < 0).map((n) => n.year);
  const falls = countFalls(core);
  const note = document.createElement('p');
  note.className = 'note warn';
  note.innerHTML = `<strong>The crimson is the whole counterweight.</strong> The land engine runs
    backwards too, in every industry: nationally, ${glossTerm('capitalAppreciation', 'capital appreciation')}
    was negative in ${negNat.length} of the ${core.national.length} years —
    ${esc(negNat.map(fy).join(', '))} — and across the 32 regions the value of farm capital fell in
    ${num(falls)} of ${num(core.regions.reduce((a, r) => a + r.series.length, 0))} region-years. Wherever
    the ochre line dips below the teal one, holding the land cost that industry money that year.`;
  card.append(note);

  const caveat = document.createElement('p');
  caveat.className = 'xsmall muted';
  caveat.textContent =
    'These industry and dairy estimates carry no error term in this file, so nothing on these seven '
    + 'charts is averaged, ranked or ordered by any statistic — the tiles sit in a fixed order and each '
    + 'shows only published values, year by year. Every averaged and ranked claim on this page uses the '
    + 'regional data further down, which does carry sample sizes and standard errors. Dairy comes from a '
    + 'different survey (ADIS) and is national only; its farms are far smaller in area than the broadacre ones.';
  card.append(caveat);

  return card;
}

function sharedDomain(industry: IndustryData, names: string[]): [number, number] {
  const vals: (number | null)[] = [];
  for (const n of names) {
    for (const r of industry.industrySeries[n] ?? []) { vals.push(r.rateEx, r.rateInc); }
  }
  const [lo, hi] = extent(vals);
  // A shared scale is the entire point of a small-multiple grid: tiles on their
  // own scales would make a ±2% industry look like a ±20% one.
  return [Math.min(lo, 0) * 1.06, Math.max(hi, 0) * 1.06];
}

/**
 * One industry's two rate-of-return series, with the space between them filled.
 *
 * The fill is the land's contribution, and its colour is its SIGN — ochre above,
 * crimson below — so a reader who cannot separate the two hues still reads the
 * chart correctly from which line is on top. Segments that cross over are split
 * at the crossing point rather than being coloured by whichever end won, which
 * would paint a whole year of positive contribution crimson.
 */
function engineChart(
  rows: IndustryYear[],
  o: {
    w: number; h: number; domain: [number, number]; prov: number | null; lastSettled: number;
    label: string; detailed: boolean; idSuffix: string; industry: string;
  },
): HTMLElement {
  const pad = o.detailed
    ? { t: 16, r: 16, b: 46, l: 46 }
    : { t: 8, r: 8, b: 20, l: 30 };
  const { frame, scroll } = chartFrame();
  const svg = chartSvg(o.w, o.h, o.label);
  if (o.detailed) svg.style.minWidth = '520px';
  const hatchId = `eng-hatch-${o.idSuffix}`;
  svg.append(hatchDefs(hatchId, GRAPHITE, 0.5));

  const years = rows.map((r) => r.year);
  if (!years.length) { scroll.append(svg); return frame; }
  const xs = linearScale([years[0], years[years.length - 1]], [pad.l, o.w - pad.r]);
  const ys = linearScale(o.domain, [o.h - pad.b, pad.t]);

  // Grid and axes. The tiles hand-roll a two-tick axis; the full-size chart in
  // the overlay gets the real thing.
  if (o.detailed) {
    axisLeft(svg, ys, pad.l, { format: (v) => `${v}%`, gridTo: o.w - pad.r, title: 'per cent a year' });
    axisBottom(svg, xs, o.h - pad.b, {
      ticks: years.filter((y) => y % 5 === 0 || y === years[years.length - 1]),
      format: (y) => fy(y),
    });
  } else {
    for (const t of niceTicks(o.domain[0], o.domain[1], 3)) {
      const y = ys(t);
      if (!Number.isFinite(y)) continue;
      svg.append(el('line', { class: t === 0 ? 'zero-line' : 'grid-line', x1: pad.l, y1: y, x2: o.w - pad.r, y2: y }));
      const lab = el('text', { class: 'axis-text', x: pad.l - 4, y: y + 3, 'text-anchor': 'end' });
      lab.textContent = `${Math.round(t)}`;
      svg.append(lab);
    }
    for (const y of years.filter((v) => v % 10 === 0)) {
      const lab = el('text', { class: 'axis-text', x: finite(xs(y)), y: o.h - 6, 'text-anchor': 'middle' });
      lab.textContent = fy(y);
      svg.append(lab);
    }
  }

  const zeroY = ys(0);
  if (Number.isFinite(zeroY)) {
    svg.append(el('line', { class: 'zero-line', x1: pad.l, y1: zeroY, x2: o.w - pad.r, y2: zeroY }));
  }

  // The provisional year: hatched, and the band and both lines stop before it.
  if (o.prov != null) {
    const x0 = xs(o.prov - 0.5);
    svg.append(el('rect', {
      x: finite(x0), y: pad.t, width: Math.max(0, finite(o.w - pad.r - x0)),
      height: o.h - pad.b - pad.t, fill: `url(#${hatchId})`, opacity: 0.5,
    }));
  }

  const settled = rows.filter((r) => r.year !== o.prov);

  // ── the band, split at every crossing ──
  for (let i = 0; i + 1 < settled.length; i++) {
    const a = settled[i]; const b = settled[i + 1];
    if (a.rateEx == null || a.rateInc == null || b.rateEx == null || b.rateInc == null) continue;
    const d1 = a.rateInc - a.rateEx; const d2 = b.rateInc - b.rateEx;
    const x1 = xs(a.year); const x2 = xs(b.year);
    const quad = (xa: number, exA: number, incA: number, xb: number, exB: number, incB: number, positive: boolean) => {
      const pts = [
        [xa, ys(exA)], [xa, ys(incA)], [xb, ys(incB)], [xb, ys(exB)],
      ].map(([px, py]) => `${finite(px).toFixed(1)},${finite(py).toFixed(1)}`).join(' L');
      svg.append(el('path', {
        d: `M${pts} Z`, fill: positive ? LAND : CRIMSON, opacity: 0.3, stroke: 'none',
      }));
    };
    if (d1 === 0 && d2 === 0) continue;
    if ((d1 >= 0 && d2 >= 0) || (d1 <= 0 && d2 <= 0)) {
      quad(x1, a.rateEx, a.rateInc, x2, b.rateEx, b.rateInc, d1 + d2 >= 0);
    } else {
      // Crosses zero inside the year: split at the crossing so the sign of the
      // fill is always the sign of the contribution it is drawing.
      const t = d1 / (d1 - d2);
      const xc = x1 + (x2 - x1) * t;
      const exC = a.rateEx + (b.rateEx - a.rateEx) * t;
      quad(x1, a.rateEx, a.rateInc, xc, exC, exC, d1 > 0);
      quad(xc, exC, exC, x2, b.rateEx, b.rateInc, d2 > 0);
    }
  }

  // ── the two lines ──
  const lines: Array<{ key: 'rateEx' | 'rateInc'; colour: string; label: string }> = [
    { key: 'rateEx', colour: FARMING, label: 'what the farming earned' },
    { key: 'rateInc', colour: LAND, label: 'counting the land as well' },
  ];
  for (const l of lines) {
    const pts = settled.map((r) => ({ x: xs(r.year), y: r[l.key] == null ? null : ys(r[l.key] as number) }));
    // brokenPath, never a bridged line: if ABARES ever drops a year from one of
    // these series, the line must show the hole rather than draw through it.
    for (const d of brokenPath(pts)) {
      svg.append(el('path', {
        d, fill: 'none', stroke: l.colour, 'stroke-width': o.detailed ? 2.2 : 1.6, 'stroke-linejoin': 'round',
      }));
    }
    for (const iso of isolatedPoints(pts)) {
      svg.append(el('circle', { cx: finite(iso.x), cy: finite(iso.y), r: 2.4, fill: l.colour }));
    }
    // The provisional tail is dashed and fainter, so it can never be read as
    // part of the settled line.
    const a = settled[settled.length - 1];
    const b = o.prov != null ? rows.find((r) => r.year === o.prov) : undefined;
    if (a && b && a[l.key] != null && b[l.key] != null) {
      svg.append(el('line', {
        x1: finite(xs(a.year)), y1: finite(ys(a[l.key] as number)),
        x2: finite(xs(b.year)), y2: finite(ys(b[l.key] as number)),
        stroke: l.colour, 'stroke-width': 1.6, 'stroke-dasharray': '3 3', opacity: 0.6,
      }));
    }
  }

  // ── one hover target per year, covering the full height of its column ──
  const halfCol = Math.max(3, (xs(years[1] ?? years[0] + 1) - xs(years[0])) / 2);
  for (const r of rows) {
    if (r.rateEx == null || r.rateInc == null) continue;
    const cx = xs(r.year);
    if (!Number.isFinite(cx)) continue;
    const gap = r.rateInc - r.rateEx;
    const hit = el('rect', {
      x: cx - halfCol, y: pad.t, width: halfCol * 2, height: o.h - pad.b - pad.t,
      fill: 'transparent', class: 'mark',
    });
    markTip(hit, [
      `farming ${signedPct(r.rateEx, 1)} · land ${pts(gap, 1)}`,
      `${o.industry}, ${fy(r.year)} · counting the land, ${signedPct(r.rateInc, 1)}`,
      r.appreciation != null ? `capital appreciation ${money(r.appreciation, { long: true })} per farm` : null,
      gap < 0 ? 'The land took value away this year.' : null,
      r.year === o.prov ? 'Provisional — drawn, never counted.' : null,
      ...evidenceLines(null, null),
      'Published industry estimate; this file carries no error term for it.',
    ]);
    svg.append(hit);
    // The full-size chart marks each year so the hover target has something to
    // point at; the tiles stay clean lines.
    if (o.detailed) {
      const prov = r.year === o.prov;
      for (const [v, colour] of [[r.rateEx, FARMING], [r.rateInc, LAND]] as const) {
        const cy = ys(v as number);
        if (!Number.isFinite(cy)) continue;
        svg.append(el('circle', {
          cx, cy, r: 2.8, fill: prov ? 'none' : colour, stroke: colour,
          'stroke-width': prov ? 1.2 : 0, opacity: prov ? 0.7 : 1,
        }));
      }
    }
  }

  scroll.append(svg);
  return frame;
}

function openIndustry(
  name: string, rows: IndustryYear[], core: Core, domain: [number, number], lastSettled: number,
): void {
  openOverlay({
    panelClass: 'modal',
    label: `${name}: the two engines, year by year`,
    lockScroll: true,
    build(panel, close) {
      const head = document.createElement('div');
      head.className = 'overlay-head';
      const h = document.createElement('h2');
      h.textContent = name === 'All Broadacre' ? 'All broadacre farms in the survey' : name;
      head.append(h, closeButton(close, `Close ${name}`));
      panel.append(head);

      const sub = document.createElement('p');
      sub.className = 'small muted';
      sub.textContent = name === 'Dairy'
        ? 'Dairy comes from the ABARES dairy survey (ADIS), at national level only. Every figure is per surveyed farm.'
        : 'National estimates for this industry, per surveyed farm. The same vertical scale as the small charts.';
      panel.append(sub);

      panel.append(engineChart(rows, {
        w: 640, h: 340, domain, prov: core.provisionalYear, lastSettled,
        label: `${name}: rate of return with and without capital appreciation, ${fy(rows[0]?.year ?? 0)} onward`,
        detailed: true, idSuffix: `d-${name.replace(/[^a-z]/gi, '')}`, industry: name,
      }));

      const wrap = document.createElement('div');
      wrap.className = 'table-scroll';
      wrap.style.marginTop = 'var(--space-md)';
      wrap.innerHTML = `<table>
        <caption class="visually-hidden">${esc(name)}: rate of return by financial year, with and without capital appreciation</caption>
        <thead><tr>
          <th scope="col">Year</th>
          <th scope="col" class="num">Farming</th>
          <th scope="col" class="num">Counting the land</th>
          <th scope="col" class="num">The land's part</th>
        </tr></thead>
        <tbody>${rows.map((r) => {
        const gap = r.rateEx != null && r.rateInc != null ? r.rateInc - r.rateEx : null;
        const isProv = r.year === core.provisionalYear;
        return `<tr>
            <th scope="row" class="mono">${esc(fy(r.year))}${isProv ? ' <span class="pill">provisional</span>' : ''}</th>
            <td class="num ${(r.rateEx ?? 0) < 0 ? 'neg' : 'pos'}">${esc(signedPct(r.rateEx, 1))}</td>
            <td class="num">${esc(signedPct(r.rateInc, 1))}</td>
            <td class="num ${gap != null && gap < 0 ? 'neg' : ''}">${esc(pts(gap, 1))}</td>
          </tr>`;
      }).join('')}</tbody></table>`;
      panel.append(wrap);

      const note = document.createElement('p');
      note.className = 'xsmall muted';
      note.style.marginTop = 'var(--space-md)';
      note.textContent = `Both columns are published ABARES series; the third is the gap between them. `
        + `The provisional year is listed for completeness and excluded from every average on this site. `
        + `${SCOPE_SHORT}`;
      panel.append(note);
    },
  });
}

/* ══════════════════════════════ 2 · by zone ════════════════════════════════ */

function zoneCard(core: Core): HTMLElement {
  const card = document.createElement('section');
  card.className = 'card stack';
  const stats = zoneEngines(core);
  const counted = stats.reduce((a, z) => a + z.years, 0);
  const total = stats.reduce((a, z) => a + z.totalYears, 0);
  const hr = stats.find((z) => z.zone === 'High Rainfall');
  const ws = stats.find((z) => z.zone === 'Wheat Sheep');

  card.innerHTML = `
    <div>
      <h2 class="card-title">Which part of the country? ${glossIcon('zone')}</h2>
      <p class="card-sub">The same two engines averaged over every firm-or-fair region-year in each of
        ABARES' three agro-climatic zones. Each zone gets two bars: teal for what the farming earned in
        an average year, ochre for the percentage points the land added on top. Click a zone for its
        regions one by one.</p>
    </div>`;
  card.append(zoneChart(stats, core));

  if (hr && ws && hr.farming != null && hr.land != null && ws.land != null) {
    const note = document.createElement('p');
    note.className = 'note';
    note.innerHTML = `<strong>Read this the right way round.</strong> Almost all of the High Rainfall
      zone's return is land — but not because its land rose faster. Its land engine
      (${esc(pts(hr.land))} a year) is no bigger than the Wheat-Sheep zone's
      (${esc(pts(ws.land))}). What is missing is the farming, at
      ${esc(signedPct(hr.farming, 2))} a year. Same land story, no farm story — and saying "land is
      98% of the return there" without that sentence points the reader at the wrong cause.`;
    card.append(note);
  }

  const evidence = document.createElement('p');
  evidence.className = 'xsmall muted';
  evidence.textContent =
    `Averages over ${num(counted)} of ${num(total)} region-years: ${num(total - counted)} excluded as `
    + 'weak evidence, as carrying no estimate, or as the provisional year. Excluding the weak years is '
    + 'not free — it keeps the years with the biggest samples, so each bar leans on that zone\'s '
    + 'best-surveyed regions, and the pastoral zone loses about two thirds of its cells. Each bar\'s own '
    + 'counts are in its tooltip.';
  card.append(evidence);
  return card;
}

function zoneEngines(core: Core): ZoneEngines[] {
  const out: ZoneEngines[] = [];
  for (const zone of ZONE_ORDER) {
    const regions = core.regions.filter((r) => r.zone === zone);
    const ex: number[] = []; const land: number[] = [];
    let total = 0;
    for (const r of regions) {
      for (const s of r.series as Cell[]) {
        total++;
        // The two site-wide rules for an average, in three lines: never the
        // provisional year, never weaker than fair evidence, never a half-pair.
        if (s.year === core.provisionalYear) continue;
        if (!isRankable(s.tier ?? null)) continue;
        if (s.rateEx == null || s.rateInc == null) continue;
        ex.push(s.rateEx);
        land.push(s.rateInc - s.rateEx);
      }
    }
    out.push({
      zone, regions: regions.length, years: ex.length, totalYears: total,
      farming: mean(ex), land: mean(land),
    });
  }
  return out;
}

function zoneChart(stats: ZoneEngines[], core: Core): HTMLElement {
  const W = 560; const rowH = 84;
  const pad = { t: 14, r: 68, b: 44, l: 76 };
  const H = pad.t + stats.length * rowH + pad.b;
  const { frame, scroll } = chartFrame();
  const svg = chartSvg(W, H, 'Average yearly return from farming and from land appreciation, by agro-climatic zone');
  svg.style.minWidth = '460px';

  const [lo, hi] = extent(stats.flatMap((s) => [s.farming, s.land]));
  const xs = linearScale([Math.min(0, lo * 1.1), Math.max(hi * 1.18, 0.5)], [pad.l, W - pad.r]);
  const zeroX = xs(0);
  const ticks = niceTicks(xs.domain[0], xs.domain[1], 5);

  for (const t of ticks) {
    const x = xs(t);
    if (!Number.isFinite(x)) continue;
    svg.append(el('line', { class: t === 0 ? 'zero-line' : 'grid-line', x1: x, y1: pad.t, x2: x, y2: H - pad.b }));
  }
  axisBottom(svg, xs, H - pad.b, {
    ticks, format: (v) => `${v}%`, x0: pad.l, x1: W - pad.r,
    title: 'per cent a year (percentage points, for the land bar)',
  });

  stats.forEach((s, i) => {
    const top = pad.t + i * rowH;
    const g = el('g', { class: 'mark', tabindex: '0', role: 'button' });
    g.setAttribute('aria-label', `${s.zone} zone — open the list of its ${s.regions} regions`);
    g.style.cursor = 'pointer';

    const name = el('text', { x: 2, y: top + 14, 'font-size': 13, 'font-weight': 640, fill: 'var(--text-primary)' });
    name.textContent = s.zone === 'Wheat Sheep' ? 'Wheat-Sheep' : s.zone;
    const sub = el('text', { class: 'axis-text', x: 2, y: top + 30 });
    sub.textContent = `${s.regions} regions`;
    g.append(name, sub);

    const bars: Array<{ v: number | null; colour: string; label: string; what: string }> = [
      { v: s.farming, colour: FARMING, label: 'farming', what: 'average published rate of return excluding capital appreciation' },
      { v: s.land, colour: LAND, label: 'land', what: 'average gap between the two published rate-of-return series' },
    ];
    bars.forEach((b, j) => {
      const y = top + 22 + j * 26;
      if (b.v == null) {
        // No estimate is drawn as a fixed-size neutral token, never as a bar of
        // length zero, which would read as "earned nothing".
        const token = el('rect', { x: finite(zeroX), y, width: 10, height: 18, fill: NEUTRAL, opacity: 0.45 });
        markTip(token, ['No reliable estimate', `${s.zone} zone — ${b.label}`, ...evidenceLines(null, 5)]);
        g.append(token);
        return;
      }
      // Crimson only below zero, so hue stays redundant with which side of the
      // zero rule the bar sits on and nothing is lost to a colour-blind reader.
      const colour = b.v < 0 ? CRIMSON : b.colour;
      const x0 = Math.min(zeroX, xs(b.v));
      const rect = el('rect', {
        x: finite(x0), y, width: Math.max(1, finite(Math.abs(xs(b.v) - zeroX))), height: 18, fill: colour, rx: 2,
      });
      markTip(rect, [
        `${b.label === 'land' ? pts(b.v) : signedPct(b.v, 2)} a year — what the ${b.label} ${b.label === 'land' ? 'added' : 'earned'}`,
        `${s.zone} zone, ${s.regions} regions`,
        b.what,
        `${s.years} of ${s.totalYears} region-years counted; ${s.totalYears - s.years} excluded as weak, absent or provisional`,
        'Firm and fair evidence only.',
      ]);
      const val = el('text', {
        x: finite(Math.max(zeroX, xs(b.v))) + 6, y: y + 13, 'font-size': 12,
        'font-weight': 600, fill: colour, class: 'mono',
      });
      val.textContent = b.label === 'land' ? pts(b.v) : signedPct(b.v, 2);
      g.append(rect, val);
    });

    const open = () => openZone(s, core);
    g.addEventListener('click', open);
    g.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open(); }
    });
    svg.append(g);
  });

  scroll.append(svg);
  return frame;
}

function openZone(s: ZoneEngines, core: Core): void {
  const rows = core.regions
    .filter((r) => r.zone === s.zone)
    .map((r) => regionEngines(r as Reg, core))
    .sort((a, b) => (b.farming ?? -99) - (a.farming ?? -99));

  openOverlay({
    panelClass: 'modal',
    label: `${s.zone} zone`,
    lockScroll: true,
    build(panel, close) {
      const head = document.createElement('div');
      head.className = 'overlay-head';
      const h = document.createElement('h2');
      h.textContent = `${s.zone === 'Wheat Sheep' ? 'Wheat-Sheep' : s.zone} zone`;
      head.append(h, closeButton(close, `Close the ${s.zone} zone list`));
      panel.append(head);

      const intro = document.createElement('p');
      intro.className = 'small muted';
      intro.textContent = `${s.regions} regions. Zone average: farming ${signedPct(s.farming, 2)} a year, `
        + `land ${pts(s.land)} a year, over ${s.years} firm-or-fair region-years of ${s.totalYears}. `
        + 'Ordered by what the farming earned. Click a region for its full dossier.';
      panel.append(intro);

      const wrap = document.createElement('div');
      wrap.className = 'table-scroll';
      wrap.style.marginTop = 'var(--space-md)';
      wrap.innerHTML = `<table>
        <thead><tr>
          <th scope="col">Region</th>
          <th scope="col" class="num">Farming</th>
          <th scope="col" class="num">Land</th>
          <th scope="col" class="num">Years used</th>
          <th scope="col">Evidence</th>
        </tr></thead>
        <tbody>${rows.map((e) => {
        const pre = tierPrefix(e.tier);
        return `<tr class="clickable" data-region="${esc(e.region.name)}" tabindex="0" role="button">
            <td>${esc(e.region.name)}</td>
            <td class="num ${e.farming != null && e.farming < 0 ? 'neg' : 'pos'}">${e.farming == null ? '—' : esc(pre + signedPct(e.farming, 2))}</td>
            <td class="num">${e.land == null ? '—' : esc(pre + pts(e.land))}</td>
            <td class="num">${e.years} / ${e.totalYears}</td>
            <td class="small">${esc(TIER_WORD[e.tier])}</td>
          </tr>`;
      }).join('')}</tbody></table>`;
      panel.append(wrap);

      // Delegated, and the listener dies with the overlay's DOM.
      const go = (t: EventTarget | null) => {
        const row = (t as Element | null)?.closest?.('[data-region]');
        const nm = row?.getAttribute('data-region');
        if (!nm) return;
        close();
        openRegion(nm);
      };
      wrap.addEventListener('click', (e) => go(e.target));
      wrap.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); go(e.target); }
      });

      const note = document.createElement('p');
      note.className = 'xsmall muted';
      note.style.marginTop = 'var(--space-md)';
      note.textContent = 'A dash means the survey supports no average for that region at all. A tilde marks '
        + 'an average built on weak evidence — read the direction, not the number. '
        + `${SCOPE_SHORT}`;
      panel.append(note);
    },
  });
}

/* ═════════════════════════════ 3 · by region ═══════════════════════════════ */

function scatterCard(core: Core, placed: Engines[], unplaced: Engines[], total: number): HTMLElement {
  const card = document.createElement('section');
  card.className = 'card stack';
  card.innerHTML = `
    <div>
      <h2 class="card-title">Which region? ${glossIcon('rse')}</h2>
      <p class="card-sub">One dot per region, and the only chart on this site that puts both engines on
        their own axis. Left to right: what the farming earned in an average year. Bottom to top: what
        the land added, in percentage points. A dot above the diagonal is a region where the land
        out-earned the farming. Colour is the agro-climatic zone; a hollow dot means weak evidence.
        Click any dot for that region's dossier.</p>
    </div>`;
  card.append(scatterChart(placed, core));

  const legend = document.createElement('div');
  legend.className = 'legend';
  legend.innerHTML = ZONE_ORDER.map((z) =>
    `<span class="legend-item"><span class="legend-swatch" style="background:${esc(ZONE_COLOUR[z] ?? NEUTRAL)}"></span>${esc(z === 'Wheat Sheep' ? 'Wheat-Sheep zone' : `${z} zone`)}</span>`).join('')
    + `<span class="legend-item"><span class="legend-swatch" style="background:transparent;border:1.6px solid ${esc(GRAPHITE)}"></span>hollow: weak evidence — read the direction</span>`
    + `<span class="legend-item"><span class="legend-swatch" style="background:${esc(CRIMSON)};opacity:.25"></span>farming below zero</span>`;
  card.append(legend);

  const evidence = document.createElement('p');
  evidence.className = 'xsmall muted';
  evidence.textContent =
    `${placed.length} of ${total} regions are placed. A region is placed only if at least `
    + `${MIN_RANKABLE_YEARS} of its years carry firm or fair evidence outside the provisional year. `
    + 'Three Northern Territory regions enter the survey in 1991–92, so their denominator is 34 years, '
    + 'not 36 — each region is only ever measured from its own first surveyed year.';
  card.append(evidence);

  if (unplaced.length) {
    const list = document.createElement('div');
    list.className = 'note';
    list.innerHTML = `<strong>${unplaced.length} regions have no average worth drawing.</strong>
      They are named rather than plotted, because a dot near zero would read as "earned nothing" when
      the truth is "the survey cannot say".
      <ul class="small" style="margin:.4rem 0 0 1.1rem">${unplaced.map((u) =>
      `<li>${esc(u.region.name)} — ${u.years} firm-or-fair year${u.years === 1 ? '' : 's'} of ${u.totalYears}
         (${esc(TIER_WORD[u.tier])} evidence overall)</li>`).join('')}</ul>`;
    card.append(list);
  }
  return card;
}

function computeEngines(core: Core): Engines[] {
  return (core.regions as Reg[])
    .map((r) => regionEngines(r, core))
    .sort((a, b) => (a.farming ?? 99) - (b.farming ?? 99));
}

function regionEngines(region: Reg, core: Core): Engines {
  const ex: number[] = []; const land: number[] = [];
  for (const s of region.series as Cell[]) {
    if (s.year === core.provisionalYear) continue;
    if (!isRankable(s.tier ?? null)) continue;
    if (s.rateEx == null || s.rateInc == null) continue;
    ex.push(s.rateEx);
    land.push(s.rateInc - s.rateEx);
  }
  return {
    region,
    farming: ex.length ? mean(ex) : null,
    land: land.length ? mean(land) : null,
    years: ex.length,
    totalYears: region.series.length,
    tier: (region.tier ?? 5) as Tier,
    placed: ex.length >= MIN_RANKABLE_YEARS,
  };
}

function scatterChart(rows: Engines[], core: Core): HTMLElement {
  const W = 780; const H = 470;
  const pad = { t: 24, r: 26, b: 60, l: 64 };
  const { frame, scroll } = chartFrame();
  const svg = chartSvg(W, H, 'Every placed region by what its farming earned against what its land added, per year');
  // A viewBox scaled down to 375px would render 11px axis text at 5px, so the
  // chart keeps a real minimum width and its wrapper scrolls instead.
  svg.style.minWidth = '620px';

  const [fLo, fHi] = extent(rows.map((r) => r.farming));
  const [lLo, lHi] = extent(rows.map((r) => r.land));
  const xs = linearScale([Math.min(0, fLo) - 0.5, Math.max(0, fHi) + 0.5], [pad.l, W - pad.r]);
  const ys = linearScale([Math.min(0, lLo) - 0.5, Math.max(0, lHi) + 0.5], [H - pad.b, pad.t]);

  const zx = xs(0);
  if (zx > pad.l) {
    svg.append(el('rect', {
      x: pad.l, y: pad.t, width: finite(zx - pad.l), height: H - pad.b - pad.t, fill: CRIMSON, opacity: 0.07,
    }));
    const t = el('text', { class: 'axis-text', x: pad.l + 5, y: H - pad.b - 8, fill: CRIMSON });
    t.textContent = 'farming lost money';
    svg.append(t);
  }

  axisLeft(svg, ys, pad.l, { format: (v) => `${v}`, gridTo: W - pad.r, title: 'what the land added (points a year)' });
  axisBottom(svg, xs, H - pad.b, { format: (v) => `${v}%`, title: 'what the farming earned (per cent a year)' });

  // The diagonal: above it the land engine out-earned the farming engine.
  const dLo = Math.max(xs.domain[0], ys.domain[0]);
  const dHi = Math.min(xs.domain[1], ys.domain[1]);
  if (dHi > dLo) {
    svg.append(el('line', {
      x1: finite(xs(dLo)), y1: finite(ys(dLo)), x2: finite(xs(dHi)), y2: finite(ys(dHi)),
      stroke: GRAPHITE, 'stroke-width': 1.2, 'stroke-dasharray': '5 4', opacity: 0.6,
    }));
    const t = el('text', { class: 'axis-text', x: finite(xs(dHi)) - 6, y: finite(ys(dHi)) - 8, 'text-anchor': 'end' });
    t.textContent = 'above this line, the land out-earned the farming';
    svg.append(t);
  }

  // Only the extremes are labelled — 26 labels would be unreadable, and every
  // dot carries its name on hover and on click.
  const byFarm = [...rows].sort((a, b) => (a.farming ?? 0) - (b.farming ?? 0));
  const byLand = [...rows].sort((a, b) => (a.land ?? 0) - (b.land ?? 0));
  const labelled = new Set<string>([
    ...byFarm.slice(0, 2).map((r) => r.region.name),
    ...byFarm.slice(-2).map((r) => r.region.name),
    ...byLand.slice(0, 1).map((r) => r.region.name),
    ...byLand.slice(-1).map((r) => r.region.name),
  ]);

  for (const e of rows) {
    if (e.farming == null || e.land == null) continue;
    const cx = xs(e.farming); const cy = ys(e.land);
    if (!Number.isFinite(cx) || !Number.isFinite(cy)) continue;

    const zoneHue = ZONE_COLOUR[e.region.zone] ?? NEUTRAL;
    const outline = isOutlineOnly(e.tier);
    // Tier 4 never encodes magnitude, so it is a fixed-size token rather than a
    // dot that might be read as a measurement. No tier-4 region clears
    // MIN_RANKABLE_YEARS today; this is the rule, not decoration.
    const token = isTokenSized(e.tier);

    const g = el('g', { class: 'mark', tabindex: '0', role: 'button' });
    g.style.cursor = 'pointer';
    const node = token
      ? el('rect', { x: cx - 5, y: cy - 5, width: 10, height: 10, fill: NEUTRAL, opacity: tierOpacity(e.tier) })
      : el('circle', {
        cx, cy, r: 7,
        fill: outline ? 'none' : collapse(zoneHue, e.tier, NEUTRAL),
        stroke: outline ? zoneHue : 'var(--bg-panel)',
        'stroke-width': outline ? 2 : 1.2,
        opacity: tierOpacity(e.tier),
      });

    const pre = tierPrefix(e.tier);
    const own = ownMultiple(e.region, core);
    markTip(g, [
      `farming ${pre}${signedPct(e.farming, 2)} a year · land ${pre}${pts(e.land)} a year`,
      `${e.region.name} — ${e.region.zone} zone`,
      own ? `its land value ×${own.mult.toFixed(2)} from ${fy(own.from)} to ${fy(own.to)}` : null,
      `${e.years} of ${e.totalYears} years counted (firm or fair, provisional excluded)`,
      ...evidenceLines(e.region.medianSample, e.tier),
      'Click for the full dossier.',
    ]);
    g.append(el('circle', { cx, cy, r: 13, fill: 'transparent' }), node);

    const open = () => openRegion(e.region.name);
    g.addEventListener('click', open);
    g.addEventListener('keydown', (ev) => {
      if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); open(); }
    });
    svg.append(g);

    if (labelled.has(e.region.name)) {
      const text = shortName(e.region.name);
      // A label on the right-most dot runs off the viewBox and is simply lost,
      // so it flips to the other side of its dot. 5.7px/char is a measured
      // approximation for the 11px axis face — close enough to choose a side.
      const flip = cx + 11 + text.length * 5.7 > W - 4;
      const t = el('text', {
        class: 'axis-text', x: flip ? cx - 11 : cx + 11, y: cy + 4,
        'text-anchor': flip ? 'end' : 'start', fill: 'var(--text-secondary)',
      });
      t.textContent = text;
      svg.append(t);
    }
  }

  scroll.append(svg);
  return frame;
}

/* ════════════════════════════════ helpers ═════════════════════════════════ */

/**
 * The land engine in percentage POINTS, never with a per cent sign.
 *
 * It is a difference between two rates, not a rate: "the land added 3.07% a
 * year" invites the reader to compound it, and it is not a thing that
 * compounds. The ASCII minus matches `signedPct` and `money` so a single
 * tooltip never mixes two kinds of minus sign.
 */
function pts(v: number | null | undefined, dp = 2): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${v > 0 ? '+' : ''}${v.toFixed(dp)} pts`;
}

function mean(v: number[]): number | null {
  if (!v.length) return null;
  return v.reduce((a, b) => a + b, 0) / v.length;
}

function countFalls(core: Core): number {
  let n = 0;
  for (const r of core.regions) for (const s of r.series) if (s.appreciation != null && s.appreciation < 0) n++;
  return n;
}

/**
 * How far a region's land value multiplied, against ITS OWN first surveyed year.
 *
 * `region.firstYear` is load-bearing: NT Barkly Tablelands, NT Top End Darwin and
 * NT Victoria River District enter the survey in 1991–92. Rebasing every region
 * on 1989–90 would divide their series by a value that does not exist. This
 * finds the first year that actually carries a value and reports which year that
 * was, so the readout can never imply a base year the region never had. The
 * provisional year is excluded from both ends.
 */
function ownMultiple(region: Reg, core: Core): { from: number; to: number; mult: number } | null {
  const cells = region.series.filter((s) => s.year !== core.provisionalYear);
  const first = cells.find((s) => s.landValue != null && s.landValue > 0);
  const last = [...cells].reverse().find((s) => s.landValue != null && s.landValue > 0);
  if (!first || !last || first.year === last.year) return null;
  return { from: first.year, to: last.year, mult: (last.landValue as number) / (first.landValue as number) };
}

/** Region names run to 45 characters; a scatter label has to be readable. */
function shortName(name: string): string {
  const cut = name.replace(/\s*\(.*\)$/, '');
  return cut.length > 26 ? `${cut.slice(0, 25).trimEnd()}…` : cut;
}
